import type { MetricPeriodType } from "~/graphql/generated/graphql";
import type translationsDA from "~/translations/da.json";
import type translationsEN from "~/translations/en.json";
import moment, { type Moment } from "moment";
import { fitDate, getSetValue } from "~/helpers/modules/accounts-helpers";
import { type AreaUnits, convertArea as turfConvertArea } from "@turf/helpers";

export const useThrottle = (func: (...args: any) => any, limit: number) => {
  let inThrottle: boolean = false;

  return function (this: any, ...args: any[]) {
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

export const getCurrentPeriod = <T extends MetricPeriodType | undefined>(periodType: T) =>
  (periodType ? fitDate(moment.utc(), periodType) : undefined) as T extends MetricPeriodType ? Moment : undefined;

export const arrayLast = <T, D extends T | undefined>(arr: T[], defVal?: D): D extends undefined ? T | undefined : T => arr[arr.length - 1] ?? (defVal as T);

export const hasValue = <T extends unknown>(v: T): v is NonNullable<T> => typeof v !== "undefined" && v !== null;

export const filterWithValue = <T>(arr: Nullable<T>[]) => arr.filter(hasValue) as T[];
export const filterUnique = <T extends string | number>(arr: T[]) => arr.filter((item, index, arr) => arr.indexOf(item) === index);
export const filterUniqueOn = <T extends Object>(arr: T[], on: (item: T) => any) =>
  arr.filter((a, index, arr) => arr.findIndex((b) => on(a) === on(b)) === index);

type MapReduceAllHandler<T, R> = (index: number, ...items: T[]) => R;
type MapReduceAllArgs<T, R> = [...arrays: T[][], handler: MapReduceAllHandler<T, R>];

export const mapReduceAll = <T, R>(...args: MapReduceAllArgs<T, R>) => {
  const arrays = args.slice(0, args.length - 1) as T[][];
  const handler = arrayLast(args) as MapReduceAllHandler<T, R>;
  const length = arrays[0].length;

  if (length === 0) return [];

  if (!arrays.every((arr) => arr.length === length)) {
    throw "mapAll array length mismatch";
  }

  const reduced: R[] = Array(length);

  for (let i = 0; i < length; i++) {
    reduced[i] = handler(i, ...arrays.map((arr) => arr[i]));
  }

  return reduced;
};

export const arrayMutateToggleValue = (arr: string[], v: string, include?: boolean) => {
  const index = arr.indexOf(v);

  const isInArray = index !== -1;
  const shouldAdd = hasValue(include) ? include : index === -1;
  const shouldRemove = hasValue(include) ? !include : index !== -1;

  if (!isInArray && shouldAdd) arr.push(v);
  else if (isInArray && shouldRemove) arr.splice(index, 1);

  return arr;
};

export const arrayCloneToggleValue = (arr: string[], v: string, include?: boolean) => {
  const clone = cloneArray(arr);

  arrayMutateToggleValue(clone, v, include);

  return clone;
};

export const refArrayToggleValue = (ref: Ref<string[]>, v: string, include?: boolean) => {
  ref.value = arrayCloneToggleValue(ref.value, v, include);
};

export const sleep = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

class NaNError extends Error {
  override message: string = "Not a number";
}

export const prepareNumber = (number: Nullable<number | string>) => {
  if (!hasValue(number)) throw new NaNError();

  if (typeof number === "string") number = parseFloat(number);

  if (!Number.isFinite(number) || isNaN(number)) throw new NaNError();

  return number;
};

export const formatString = (string: string | null | undefined, formatter: (string: string) => string) => {
  if (!hasValue(string)) return "–";

  return formatter(string);
};

export const formatNumber = (number: number | string | null | undefined, formatter: (number: number) => string) => {
  try {
    return formatter(prepareNumber(number));
  } catch (e) {
    if (e instanceof NaNError) return "–";
    else throw e;
  }
};

export type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

export const getEntries = <T extends object>(obj: T) => Object.entries(obj) as Entries<T>;

export const objectFromKeys = <T extends Record<string, any>, K extends (keyof T)[]>(o: T, ...keys: K) =>
  Object.fromEntries(keys.map((k) => [k, o[k]])) as { [k in K[number]]: T[k] };

export const keysTyped = <T extends Object>(obj: T) => Object.keys(obj) as (keyof T)[];

export type TranslationKey = keyof typeof translationsDA & keyof typeof translationsEN;

export const asDataItems = <T extends { [k in string]: any }>(
  items: Nullable<T[]>,
  idField: KeyOfType<T, string>,
  nameField: ((item: T) => Nullable<string>) | KeyOfType<T, Nullable<string> | string>
): DataItem[] => items?.map((item) => ({ id: item[idField], name: (typeof nameField === "function" ? nameField(item) : item[nameField]) ?? "" })) ?? [];

/** colors */
export const clamp = (min: number, max: number, v: number) => Math.min(Math.max(v, min), max);
export const clampNorm = (min: number, max: number, v: number) => (clamp(min, max, v) - min) / (max - min);

export type PeriodUpdateEvent = { dateFrom: string | undefined; dateTo: string | undefined; periodType: MetricPeriodType | undefined };

export const stringReplaceExceptLast = (str: string, pattern: string, replaceWith: string) => {
  const regex = new RegExp(`${pattern}(?=.*${pattern})`, "g");

  return str.replace(regex, replaceWith);
};

export const flattenTree = <T extends { children?: T[] }>(tree: T) => {
  const flat: T[] = [tree];

  const children = tree.children ?? [];

  for (let i = 0; i < children.length; i++) {
    flat.push(...flattenTree(children[i]));
  }

  return flat;
};

export const pushGet = <T>(array: T[], item: T) => array[array.push(item) - 1];

export const getSet = <T>(array: T[], accessor: (item: T) => boolean, create: () => T) => array.find(accessor) ?? pushGet(array, create());

export const sum = (array: Nullable<number>[]) => array.reduce<number>((curr, next) => curr + (next ?? 0), 0);

interface GroupByConfigBase<T extends Object> {
  type: GroupByConfigType<T>;
  getKey: GroupByConfigValue<T>;
}

interface PeriodGroupByConfig<T extends Object> extends GroupByConfigBase<T> {
  type: "period";
  getKey: (item: T) => Nullable<Moment>;
}

interface StringGroupByConfig<T extends Object> extends GroupByConfigBase<T> {
  type: "string";
  getKey: (item: T) => Nullable<string>;
}

type GroupByConfigType<T extends Object> = GroupByConfig<T>["type"];
type GroupByConfigValue<T extends Object> = GroupByConfig<T>["getKey"];

type GroupByConfig<T extends Object> = StringGroupByConfig<T> | PeriodGroupByConfig<T>;

/** Creates all combinations of  */
const combinator = <T>(options: T[]) => {
  const combinations: T[][] = new Array();
  for (let i = 0; i < options.length; i++) {
    for (let j = i + 1; j < options.length + 1; j++) {
      combinations.push(options.slice(i, j));
    }
  }
  return combinations;
};

export const groupBy = <T extends Object, GroupBys extends { [k in string]: GroupByConfig<T> }, GK extends string & keyof GroupBys>(
  items: T[],
  groupBys: GroupBys
) => {
  const groupsMap: Map<
    string,
    {
      [k in GK]: GroupBys[k]["type"] extends "period" ? Moment : string;
    } & { items: T[] }
  > = new Map();

  const keys = Object.fromEntries(Object.keys(groupBys).map((k) => [k, new Set<string>()])) as {
    [k in GK]: Set<string>;
  };

  const getStringHash = (key: GK, value: string) => `${key}:${value}`;
  const getPeriodHash = (key: GK, value: Moment) => `${key}:${value.format("YYYY-MM-DD")}`;

  const buildHash = (hashes: string[]) => hashes.sort((a, b) => a.localeCompare(b)).join("___");

  for (let item of items) {
    const hashes: string[] = [];

    const creators: (() => { [k in string]: any })[] = [];

    for (let _key in groupBys) {
      const key = _key as any as GK;

      const groupBy = groupBys[key]!;

      switch (groupBy.type) {
        case "period":
          const period = groupBy.getKey(item);

          if (hasValue(period)) {
            hashes.push(getPeriodHash(key, period));

            creators.push(() => ({ [key]: period }));

            keys[key].add(period.format("YYYY-MM-DD"));
          }
          break;
        case "string":
          const string = groupBy.getKey(item);

          if (hasValue(string)) {
            hashes.push(getStringHash(key, string));

            creators.push(() => ({ [key]: string }));

            keys[key].add(string);
          }
          break;
      }
    }

    const create = () =>
      ({
        ...creators.reduce((curr, next) => ({ ...curr, ...next() }), {}),
        items: [],
      } as any);

    const hash = buildHash(hashes);

    const group: { items: T[] } | undefined = hash ? getSetValue(hash, groupsMap, create) : undefined;

    group?.items.push(item);

    /**Note Sven: needed if we want to allow "get" called partial */
    // const hashCombinations = combinator(hashes);

    // for (let combination of hashCombinations) {
    //   const hash = buildHash(combination);

    //   const group: { items: T[] } | undefined = hash ? getSetValue(hash, groupsMap, create) : undefined;

    //   group?.items.push(item);
    // }
  }

  const get = (by: {
    [k in GK]: GroupBys[k]["type"] extends "period" ? Moment : string;
  }) => {
    const hashes: string[] = [];
    let key: GK;

    for (key in by) {
      const { type } = groupBys[key];
      const value = by[key];

      if (!value) return;

      switch (type) {
        case "period":
          const period = value as Moment;

          hashes.push(getPeriodHash(key, period));
          break;
        case "string":
          const string = value as string;
          hashes.push(getStringHash(key, string));
          break;
      }
    }

    return groupsMap.get(buildHash(hashes));
  };

  return {
    map: groupsMap,
    get,
    keys,
  };
};

export const inlineSwitch = <T extends string | number | symbol, R>(value: T, options: { [k in T]: () => R }): R => options[value]();

export const cloneArray = <T extends any[]>(arr: T): T => arr.map((i) => (typeof i === "object" ? cloneDeep(i) : i)) as T;

export const cloneDeep = <T extends Nullable<Object>>(o: T): T => {
  if (!hasValue(o) || typeof o !== "object") return o;
  else if (Array.isArray(o)) return cloneArray(o);

  const clone = { ...o };

  let key: keyof typeof o;

  for (key in clone) {
    const cloneCast = clone as { [k in typeof key]: Object };

    const value = cloneCast[key];

    if (typeof value === "object") cloneCast[key] = cloneDeep(value);
  }

  return clone;
};

export const stringSplitWithParts = (string: string, splitBy: Nullable<string>): { before?: string; after?: string; middle: string } => {
  const matchStart = hasValue(splitBy) ? string.toLowerCase().indexOf(splitBy.toLowerCase()) : undefined;
  const matchEnd = hasValue(splitBy) ? matchStart! + splitBy.length : undefined;

  return {
    before: hasValue(matchStart) ? string.substring(0, matchStart) : undefined,
    middle: hasValue(matchStart) && hasValue(matchEnd) ? string.substring(matchStart, matchEnd) : string,
    after: hasValue(matchEnd) ? string.substring(matchEnd) : undefined,
  };
};

export const arrayUpdateId = <T extends { id: string }>(arr: T[], newItem: T): T[] => {
  const index = arr.findIndex((i) => i.id === newItem.id);

  if (index === -1) return arr;

  const clone = cloneArray(arr);

  clone[index] = newItem;

  return clone;
};

export const arrayRefUpdateId = <T extends { id: string }>(arrRef: Ref<T[]>, newItem: T) => (arrRef.value = arrayUpdateId(arrRef.value, newItem));

export const waitUntil = async (ref: MaybeRefOrGetter<boolean>) => {
  return new Promise<void>((res) => {
    if (toValue(ref)) {
      res();
      return;
    }

    const stop = watch(
      () => toValue(ref),
      async (newValue) => {
        if (newValue) {
          res();

          await nextTick();

          stop();
        }
      }
    );
  });
};

const areaUnitsTurfMap: { [k: string]: AreaUnits } = {
  sqm: "meters",
  sqft: "feet",
  m2: "meters",
  ft2: "feet",
};

export const convertArea = (area: Nullable<number | string>, sourceUnit: Nullable<string>, targetUnit: Nullable<string>) => {
  if (!sourceUnit || !targetUnit) {
    if (area) throw `missing area units, sourceUnit: ${sourceUnit}, targetUnit: ${targetUnit}, area: ${area}`;
    else return 0;
  }

  const turfSourceUnit = areaUnitsTurfMap[sourceUnit.toLowerCase()];
  const turfTargetUnit = areaUnitsTurfMap[targetUnit.toLowerCase()];

  if (area && (!turfSourceUnit || !turfTargetUnit)) throw `unsupported area units sourceUnit: ${sourceUnit}, targetUnit: ${targetUnit}`;

  try {
    return turfConvertArea(prepareNumber(area), turfSourceUnit, turfTargetUnit);
  } catch {
    return 0;
  }
};

type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}` ? `${T extends Capitalize<T> ? "_" : ""}${Lowercase<T>}${CamelToSnakeCase<U>}` : S;

type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}` ? `${T}${Capitalize<SnakeToCamelCase<U>>}` : S;

export const camelToSnakeCase = <T extends string>(str: T) => {
  const result = str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`) as CamelToSnakeCase<T>;

  return result;
};

export const snakeToCamelCase = <T extends string>(str: T) => {
  return str.replace(/_./g, (match) => match[1].toUpperCase()) as SnakeToCamelCase<T>;
};

export const uppercase = <T extends string>(s: T) => s.toUpperCase() as Uppercase<T>;
export const lowercase = <T extends string>(s: T) => s.toLowerCase() as Lowercase<T>;

export const mapTree = <T extends { parent: T | undefined; children: T[] }, R extends { parent: R | undefined; children: R[] }>(
  tree: T,
  map: (node: T) => Omit<R, "parent" | "children">
): R => {
  const clone = cloneDeep(tree);

  const mapNode = (node: T) => {
    const mapped = map(node) as R;

    mapped.parent = node.parent as R | undefined;
    mapped.children = node.children.map(mapNode);

    return mapped;
  };

  return mapNode(clone);
};

/** Normalizes a value that may be a single item or an array into a `T[]`. */
export const toArray = <T>(value: T | T[]): T[] => (Array.isArray(value) ? value : [value]);
